import { Component, Input } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { CompanyListsActionsComponent } from './company-lists.actions.component';
import { CompanyListsService }          from './company-lists.service';
import { ConfirmService }               from '../messages/confirm/confirm.service';
import { CustomerSourceModel }          from './typings/customer-source';
import { MessagesService }              from '../messages/messages.service';
import { RemoveComponent }              from '../table/cell-components/actions/remove.component';
import { Table }                        from '../table/types/table';
import { TableTextControlComponent }    from '../table/cell-components/controls/text-control/table-text-control.component';

@Component({
  selector   : 'wor-company-lists-customer-sources',
  template: require('./company-lists.customer-sources.component.html')
})
export class CompanyListsCustomerSourcesComponent {
  @Input() customerSources: Array<CustomerSourceModel> = [];

  originalSources: Map<string, CustomerSourceModel> = new Map();
  tableSettings  : Table                            = {
    columns: [
      {
        component: {
          bindings: {
            outputs: {
              onRemoveClick: ( customerSource : CustomerSourceModel ) => this.deleteCustomerSource(customerSource)
            }
          },
          enabled: true,
          factory: RemoveComponent
        },
        sortable: false
      },
      {
        component: {
          bindings: {
            inputs: {
              disabled : ( customerSource : CustomerSourceModel ) => !customerSource.editing || !!customerSource.id,
              giveFocus: ( customerSource : CustomerSourceModel ) => customerSource.focusField === 'code',
              model    : 'code',
              name     : 'customer_source_code',
              required : true
            },
            outputs: {
              focus: ( customerSource : CustomerSourceModel ) => this.clearFocusField(customerSource, 'code')
            }
          },
          enabled: true,
          factory: TableTextControlComponent
        },
        field : 'code',
        header: {
          text: this.translateService.instant('COMPANY_SPACE.SETTINGS.CODE')
        }
      },
      {
        component: {
          bindings: {
            inputs: {
              disabled : ( customerSource : CustomerSourceModel ) => !customerSource.editing,
              giveFocus: ( customerSource : CustomerSourceModel ) => customerSource.focusField === 'description',
              model    : 'description',
              name     : 'customer_source_description',
              required : true
            },
            outputs: {
              focus: ( customerSource : CustomerSourceModel ) => this.clearFocusField(customerSource, 'description')
            }
          },
          enabled: true,
          factory: TableTextControlComponent
        },
        field : 'description',
        header: {
          text: this.translateService.instant('GENERAL_SPACE.FIELD.DESCRIPTION')
        }
      },
      {
        component: {
          bindings: {
            outputs: {
              onCancelClick: ( customerSource : CustomerSourceModel ) => this.toggleEditing(customerSource, false),
              onEditClick  : ( customerSource : CustomerSourceModel ) => this.toggleEditing(customerSource, true),
              onSaveClick  : ( customerSource : CustomerSourceModel ) => this.save(customerSource)
            }
          },
          enabled: true,
          factory: CompanyListsActionsComponent
        },
        sortable: false
      }
    ],
    empty: {
      text: this.translateService.instant('COMPANY_SPACE.LISTS.GRID.NO_CUSTOMER_SOURCES')
    },
    pagination: {
      enabled: false
    },
    rows: {
      highlight: {
        enabled: false
      }
    },
    sorting: {
      enabled: false
    }
  };

  collapsed = false;

  constructor (
    private companyListsService: CompanyListsService,
    private confirmService     : ConfirmService,
    private messagesService    : MessagesService,
    private translateService   : TranslateService
  ) {}

  addCustomerSource () : void {
    this.customerSources = [
      ...this.customerSources,
      {
        code       : '',
        description: '',
        editing    : true,
        focusField : 'code'
      }
    ];
  }

  clearFocusField ( customerSource : CustomerSourceModel, field : 'code' | 'description' ) : void {
    if ( customerSource.focusField === field ) {
      customerSource.focusField = undefined;
    }
  }

  deleteCustomerSource ( customerSource : CustomerSourceModel ) : void {
    if ( !customerSource.id ) {
      this.customerSources = this.customerSources.filter(source => source !== customerSource);

      return;
    }

    this.confirmService.generic(this.translateService.instant('JS_SPACE.CONFIRM.DELETE_TEMPLATE'))
    .then(() => this.companyListsService.deleteCustomerSource(customerSource))
    .then(() => {
      this.customerSources = this.customerSources.filter(source => source.id !== customerSource.id);

      this.messagesService.show(this.translateService.instant('JS_SPACE.MESSAGES.DELETED.CUSTOMER_SOURCE'), 'success');
    })
    .catch(error => this.messagesService.error(error));
  }

  refreshTable () : void {
    this.customerSources = this.customerSources.map(customerSource => ({ ...customerSource }));
  }

  save ( customerSource : CustomerSourceModel ) : void {
    if ( !customerSource.code || !customerSource.description ) {
      this.messagesService.error(this.translateService.instant('JS_SPACE.MESSAGES.INVALID_INPUT'));

      return;
    }

    this.companyListsService.saveCustomerSource(customerSource)
    .then(( customerSources : Array<CustomerSourceModel> ) => {
      this.customerSources = customerSources.map(source => ({
        ...source,
        editing   : false,
        focusField: undefined
      }));

      this.messagesService.show(this.translateService.instant('JS_SPACE.MESSAGES.SAVED.CUSTOMER_SOURCE'), 'success');
    })
    .catch(error => this.messagesService.error(error));
  }

  toggleEditing ( customerSource : CustomerSourceModel, editing : boolean ) : void {
    if ( editing ) {
      if ( customerSource.id && !this.originalSources.has(customerSource.id) ) {
        this.originalSources.set(customerSource.id, { ...customerSource, editing: false });
      }

      customerSource.editing    = true;
      customerSource.focusField = customerSource.id
        ? 'description'
        : 'code';
    }
    else if ( !customerSource.id ) {
      this.deleteCustomerSource(customerSource);

      return;
    }
    else {
      const originalSource = this.originalSources.get(customerSource.id);

      if ( originalSource ) {
        Object.assign(customerSource, originalSource);

        this.originalSources.delete(customerSource.id);
      }

      customerSource.editing    = false;
      customerSource.focusField = undefined;
    }

    this.refreshTable();
  }
}
